import React, { useState, useEffect } from "react";
import MessengerLayout  from "../../../layout/MessengerLayout";
import UserList         from "./UserList";
import UserEditDrawer   from "./UserEditDrawer";
import AdminDocuments   from "./AdminDocuments";
import OrgChart         from "./OrgChart";
import CreateUserForm   from "./CreateUserForm";
import BulkUserUpload   from "./BulkUserUpload";
import KiraSettingsPage from "../kira/KiraSettingsPage";
import { Plus, Upload, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

export default function AdminProfilePage() {
  let user = null;
  try { user = JSON.parse(localStorage.getItem("user") || "null"); } catch {}
  const isAdmin = !!(user && (user.is_admin || user.role === "admin" || user.roles?.includes?.("admin") || user.role === "superadmin"));
  const isSuperAdmin = !!(user && (user.is_superuser || user.role === "superadmin"));

  // KIRA tab is superadmin-only — tenant admins should never see or
  // configure KIRA (we manage that centrally from the superadmin panel).
  const TABS = [
    { key: "users", label: "👥 Users"     },
    { key: "docs",  label: "📄 Documents" },
    { key: "org",   label: "🏛 Org Chart" },
    ...(isSuperAdmin ? [{ key: "kira", label: "🤖 KIRA" }] : []),
  ];

  const [tab,           setTab]           = useState("users");
  const [usersSubView,  setUsersSubView]  = useState("list"); // "list" | "create" | "bulk"
  const [editUser,      setEditUser]      = useState(null);
  const [docsUserId,    setDocsUserId]    = useState("");
  const [docsUserName,  setDocsUserName]  = useState("");
  const [listKey,       setListKey]       = useState(0); // bump to refresh list

  // Documents-tab employee picker state. `docsUsers` is a tenant-scoped
  // list reused from the Users-tab endpoint (GET /api/admin/users); we
  // fetch once on first entry to the docs tab and hold the result for
  // the page lifetime. `docsUserId` (system UUID) is the hidden id the
  // Generate endpoints consume — never rendered raw in the UI.
  const [docsUsers,        setDocsUsers]        = useState([]);
  const [docsUsersLoading, setDocsUsersLoading] = useState(false);
  const [docsUsersError,   setDocsUsersError]   = useState(null);
  const [docsQuery,        setDocsQuery]        = useState("");
  const [docsPickerOpen,   setDocsPickerOpen]   = useState(false);

  useEffect(() => {
    if (tab !== "docs" || docsUsers.length > 0 || docsUsersLoading) return;
    const token = localStorage.getItem("token");
    setDocsUsersLoading(true); setDocsUsersError(null);
    fetch(`${API_BASE}/api/admin/users?active=true`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => setDocsUsers(Array.isArray(data) ? data : data?.users || []))
      .catch(e => setDocsUsersError(e.message))
      .finally(() => setDocsUsersLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["profile_update", "emergency_contact_update", "bank_update"].includes(t)) {
        setListKey(k => k + 1);
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => window.removeEventListener("kira_action_complete", handleKiraAction);
  }, []);

  if (!isAdmin) {
    return (
      <MessengerLayout>
        <div className="p-8">
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-red-300 text-sm">
            Access denied — admin role required.
          </div>
        </div>
      </MessengerLayout>
    );
  }

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">People</h1>
          <p className="text-white/40 text-sm mt-0.5">Manage users, documents and org structure</p>
        </div>

        {/* Tab nav */}
        <div className="flex items-center gap-2 flex-wrap">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 rounded-full border transition-colors ${
                tab === t.key
                  ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                  : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
              }`}>
              {t.label}
            </button>
          ))}

          {/* Users sub-actions — only show on users tab */}
          {tab === "users" && (
            <div className="ml-auto flex gap-2">
              <button onClick={() => setUsersSubView(v => v === "create" ? "list" : "create")}
                className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors ${
                  usersSubView === "create"
                    ? "bg-[#00ff90]/15 border-[#00ff90]/30 text-[#00ff90]"
                    : "border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/10 hover:text-[#00ff90]"
                }`}>
                <Plus size={12} /> Create User
              </button>
              <button onClick={() => setUsersSubView(v => v === "bulk" ? "list" : "bulk")}
                className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors ${
                  usersSubView === "bulk"
                    ? "bg-blue-500/15 border-blue-500/30 text-blue-400"
                    : "border-white/10 bg-white/5 text-white/40 hover:bg-white/8 hover:text-white/70"
                }`}>
                <Upload size={12} /> Bulk Upload
              </button>
            </div>
          )}
        </div>

        {/* Users tab */}
        {tab === "users" && (
          <>
            {/* Create user inline panel */}
            {usersSubView === "create" && (
              <div className="rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] p-5">
                <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Create New User</p>
                <CreateUserForm onCreated={() => { setUsersSubView("list"); setListKey(k => k + 1); }} />
              </div>
            )}

            {/* Bulk upload inline panel */}
            {usersSubView === "bulk" && (
              <div className="rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] p-5">
                <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Bulk Upload (CSV)</p>
                <BulkUserUpload onUploaded={() => { setUsersSubView("list"); setListKey(k => k + 1); }} />
              </div>
            )}

            <UserList
              key={listKey}
              onOpenUser={u => {
                setEditUser(u);
                // Wire docs shortcut: clicking user while on docs sets their id
              }}
            />
          </>
        )}

        {/* Documents tab */}
        {tab === "docs" && (
          <>
            {/* Employee picker — searchable dropdown reusing the tenant
                user list. Replaces the prior "type a UUID" free-text bar
                (kept as AdminProfilePage.jsx.bak_docsel). The system id
                is stored in hidden state (docsUserId) and never shown. */}
            <div className="rounded-xl border border-white/10 bg-white/3 px-4 py-3 space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-white/40 shrink-0">Viewing documents for:</span>
                <div className="relative flex-1 min-w-[240px]">
                  <input
                    value={docsQuery}
                    onChange={e => { setDocsQuery(e.target.value); setDocsPickerOpen(true); }}
                    onFocus={() => setDocsPickerOpen(true)}
                    onBlur={() => setTimeout(() => setDocsPickerOpen(false), 120)}
                    placeholder={docsUsersLoading ? "Loading employees…" : "Search employees by name, department or ID…"}
                    disabled={docsUsersLoading}
                    className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors" />
                  {docsPickerOpen && docsUsers.length > 0 && (() => {
                    const q = docsQuery.trim().toLowerCase();
                    const rows = docsUsers.filter(u => {
                      if (!q) return true;
                      return (u.full_name || u.name || "").toLowerCase().includes(q)
                          || (u.employee_code || u.employee_id || "").toLowerCase().includes(q)
                          || (u.department || "").toLowerCase().includes(q);
                    });
                    return (
                      <div className="absolute z-50 top-full mt-1 left-0 right-0 max-h-72 overflow-auto rounded-lg border border-[#00ff90]/25 bg-[#0d0d0d] shadow-[0_0_20px_#00ff9022]">
                        {rows.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-white/30 italic">No matching employees</p>
                        ) : rows.slice(0, 100).map(u => {
                          const name = u.full_name || u.name || u.email || "Unknown";
                          const dept = u.department || "—";
                          const code = u.employee_code || u.employee_id || "";
                          return (
                            <button
                              key={u.id}
                              onMouseDown={e => {
                                e.preventDefault();
                                setDocsUserId(u.id);
                                setDocsUserName(name);
                                setDocsQuery("");
                                setDocsPickerOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-[#00ff90]/10 hover:text-[#00ff90] transition-colors flex items-center gap-2 border-b border-white/5 last:border-0">
                              <span className="flex-1 truncate">{name}</span>
                              <span className="text-xs text-white/40 shrink-0">{dept}</span>
                              {code && <span className="text-xs text-[#00ff90]/50 shrink-0">{code}</span>}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
              {docsUserId && (() => {
                const sel  = docsUsers.find(u => u.id === docsUserId);
                const name = (sel && (sel.full_name || sel.name)) || docsUserName || "";
                const dept = sel?.department || "";
                const code = sel?.employee_code || sel?.employee_id || "";
                return (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-white/40">Selected:</span>
                    <span className="text-[#00ff90]">{name || "(unnamed)"}</span>
                    {dept && <span className="text-white/40">· {dept}</span>}
                    {code && <span className="text-[#00ff90]/60">· {code}</span>}
                    <button
                      onClick={() => { setDocsUserId(""); setDocsUserName(""); setDocsQuery(""); }}
                      className="ml-1 text-white/30 hover:text-red-400 transition-colors flex items-center"
                      title="Clear selection">
                      <X size={12} />
                    </button>
                  </div>
                );
              })()}
              {docsUsersError && (
                <p className="text-xs text-red-400/70">⚠ Couldn't load employee list: {docsUsersError}</p>
              )}
            </div>
            <AdminDocuments userId={docsUserId} userName={docsUserName} />
          </>
        )}

        {/* Org chart tab */}
        {tab === "org" && (
          <OrgChart onSelectUser={u => setEditUser(u)} />
        )}

        {/* KIRA settings tab */}
        {tab === "kira" && <KiraSettingsPage />}

      </div>

      {/* Edit drawer — available from all tabs */}
      {editUser && (
        <UserEditDrawer
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => {
            setListKey(k => k + 1);
            // If docs tab open, carry user id across
            if (tab === "docs") {
              setDocsUserId(editUser.id || "");
              setDocsUserName(editUser.name || editUser.full_name || "");
            }
          }}
        />
      )}
    </MessengerLayout>
  );
}